// Import/Export utilities for Songwriter's Toolbox
// Handles JSON-based song export with embedded audio (base64)

import { db, generateId } from './db';

// Export format version for future compatibility
const EXPORT_VERSION = '1.0';

/**
 * Export a single song to a portable JSON format
 * Audio blobs are embedded as base64 strings
 */
export async function exportSong(song) {
    const exportData = {
        version: EXPORT_VERSION,
        exportedAt: new Date().toISOString(),
        app: 'Songwriter\'s Toolbox',
        song: {
            title: song.title,
            createdAt: song.createdAt,
            updatedAt: song.updatedAt,
            fragments: []
        }
    };

    // Process each fragment
    for (const fragment of song.fragments) {
        const exportFragment = { ...fragment };
        
        // If fragment has audio, embed as base64
        if (fragment.type === 'audio' && fragment.blobId) {
            try {
                const blob = await db.getAudioBlob(fragment.blobId);
                if (blob) {
                    const base64 = await blobToBase64(blob);
                    exportFragment.audioData = {
                        base64,
                        mimeType: blob.type || fragment.mimeType || 'audio/webm'
                    };
                }
            } catch (error) {
                console.warn('Could not export audio for fragment:', fragment.id, error);
                exportFragment.audioExportError = 'Audio could not be exported';
            }
        }
        
        exportData.song.fragments.push(exportFragment);
    }

    return exportData;
}

/**
 * Export multiple songs
 */
export async function exportSongs(songs) {
    const exportData = {
        version: EXPORT_VERSION,
        exportedAt: new Date().toISOString(),
        app: 'Songwriter\'s Toolbox',
        songs: []
    };

    for (const song of songs) {
        const songExport = await exportSong(song);
        exportData.songs.push(songExport.song);
    }

    return exportData;
}

/**
 * Import a song from exported JSON
 * Returns the created song ID
 */
export async function importSong(exportData, addFragmentFn, createSongFn) {
    // Handle both single song and multi-song format
    const songData = exportData.song || exportData;
    
    // Create new song
    const newSong = await createSongFn();
    
    // Update title if provided
    if (songData.title) {
        const song = await db.getSong(newSong.id);
        song.title = songData.title;
        // Preserve original timestamps if available
        if (songData.createdAt) {
            song.originalCreatedAt = songData.createdAt;
        }
        song.importedAt = new Date().toISOString();
        await db.saveSong(song);
    }

    // Import fragments
    for (const fragment of (songData.fragments || [])) {
        const fragmentData = { ...fragment };
        
        // Remove old IDs - will get new ones
        delete fragmentData.id;
        delete fragmentData.blobId;
        
        // Handle embedded audio
        if (fragment.audioData?.base64) {
            try {
                const blob = base64ToBlob(fragment.audioData.base64, fragment.audioData.mimeType);
                const blobId = generateId();
                await db.saveAudioBlob(blobId, blob);
                fragmentData.blobId = blobId;
                delete fragmentData.audioData;
            } catch (error) {
                console.warn('Could not import audio:', error);
                fragmentData.audioImportError = 'Audio could not be imported';
            }
        }
        
        await addFragmentFn(newSong.id, fragmentData);
    }

    return newSong.id;
}

/**
 * Import multiple songs from exported JSON
 */
export async function importSongs(exportData, addFragmentFn, createSongFn) {
    const importedIds = [];
    
    if (exportData.songs) {
        // Multi-song format
        for (const songData of exportData.songs) {
            const id = await importSong({ song: songData }, addFragmentFn, createSongFn);
            importedIds.push(id);
        }
    } else if (exportData.song) {
        // Single song format
        const id = await importSong(exportData, addFragmentFn, createSongFn);
        importedIds.push(id);
    }
    
    return importedIds;
}

/**
 * Download export data as a .stb file (JSON)
 */
export function downloadExport(exportData, filename = 'songs') {
    const json = JSON.stringify(exportData, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.stb.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

/**
 * Read and parse an import file
 */
export function readImportFile(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        
        reader.onload = (e) => {
            try {
                const data = JSON.parse(e.target.result);
                
                // Validate it's a valid export
                if (!data.app && !data.song && !data.songs) {
                    reject(new Error('Invalid file format'));
                    return;
                }
                
                resolve(data);
            } catch (error) {
                reject(new Error('Could not parse file'));
            }
        };
        
        reader.onerror = () => reject(new Error('Could not read file'));
        reader.readAsText(file);
    });
}

// Helper: Convert Blob to base64
function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            const base64 = reader.result.split(',')[1];
            resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

// Helper: Convert base64 to Blob
function base64ToBlob(base64, mimeType = 'audio/webm') {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
}
